import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useMemo } from 'react'
import { useCurrentScheduleCounts } from './useCurrentSchedule'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { useSocialCountsByType } from './useSocialCountsByType'
import { useContentTypeCosts } from './useContentTypeCosts'
export { useSocialCountsByType } from './useSocialCountsByType'
export { useContentTypeCosts } from './useContentTypeCosts'
export { useNeedsCreditsForCost } from './useNeedsCreditsForCost'

export const useCreditsToGenerateContent = (
  socialCount: number = 0,
  blogCount: number = 0,
  emailCount: number = 0,
  shortFormVideoCount: number = 0,
  stillImageCount: number = 0,
  videoCount: number = 0,
  carouselCount: number = 0,
  storyCount: number = 0,
  options: {
    applyVisualStyle?: boolean
    percentageOfVisualStylesGeneration?: number
    forceGenV2?: boolean
  } = {},
) => {
  // --- Content counts ---
  const {
    stillImages: totalStillImages,
    carousels: totalCarousels,
    videos: totalVideos,
    shortFormVideo: weightedShortForm,
  } = useSocialCountsByType(socialCount, shortFormVideoCount, stillImageCount, videoCount, carouselCount, {
    percentageOfVisualStylesGeneration: options.percentageOfVisualStylesGeneration || 0.5,
    applyVisualStyle: options.applyVisualStyle,
    forceGenV2: options.forceGenV2,
  })

  // --- Cost rates per content type ---
  const typeCosts = useContentTypeCosts({ applyVisualStyle: options.applyVisualStyle, forceGenV2: options.forceGenV2 })

  // --- Credit balance and subscription state ---
  const workspace = useCurrentWorkspace()
  const {
    creditUsageStore: { creditUsage, initialized: creditUsageInitialized },
    tierUsageStore: { currentSubscription, autopilotAddOns, initialized: tierUsageInitialized },
  } = useRootStore()
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const isTrialing = currentSubscription?.status === 'trialing'
  const autopilotContentCreditsPrice = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )?.prices[0]

  // --- Brand profile flags (used in Pioneer credit exception below) ---
  const profile = useBrandProfile({ cacheTime: 5 * 60 * 1000 })
  const hasStylizedImage =
    !!profile?.data?.brand_kit?.content_preferences?.apply_visual_style || !!options.applyVisualStyle
  const hasAnimatedImage = !!profile?.data?.brand_kit?.content_preferences?.video_sources?.includes('animated_image')

  // --- Loading state ---
  const { isFetched: isBrandPlanFetched } = useCurrentBrandPlan({ cacheTime: 5 * 60 * 1000 })
  const isProcessing = !(profile.isFetched && creditUsageInitialized && tierUsageInitialized && isBrandPlanFetched)

  // --- Credit calculation ---
  const { needsCredits, creditsNeeded, creditsRemaining, creditsNeededPerContentType, perTypeCosts } = useMemo(() => {
    if (!creditUsage || Object.keys(creditUsage).length === 0) {
      return { needsCredits: false, creditsNeeded: 0, creditsRemaining: 0 }
    }

    const creditsNeeded =
      blogCount * typeCosts.blog +
      emailCount * typeCosts.email +
      totalStillImages * typeCosts.stillImage +
      totalCarousels * typeCosts.carousel +
      totalVideos * typeCosts.feedVideo +
      weightedShortForm * typeCosts.shortFormVideo +
      storyCount * typeCosts.story

    const creditsRemaining = creditUsage.credits_remaining || 0
    const exceedsBalance = creditsNeeded > 0 && creditsNeeded > creditsRemaining

    // Pioneer users only need credits for features that cost credits on their plan
    const pioneerException = isOnPioneer && (isTrialing || (!hasAnimatedImage && !hasStylizedImage))

    const autoReloadCovers =
      creditUsage.can_auto_reload &&
      (workspace?.auto_reload_limit == null || creditsNeeded <= workspace.auto_reload_limit)

    return {
      needsCredits: exceedsBalance && !pioneerException && !autoReloadCovers,
      creditsNeeded,
      creditsRemaining,
      creditsNeededPerContentType: {
        blog: blogCount * typeCosts.blog,
        email: emailCount * typeCosts.email,
        social:
          totalStillImages * typeCosts.stillImage +
          totalCarousels * typeCosts.carousel +
          totalVideos * typeCosts.feedVideo +
          storyCount * typeCosts.story,
        shortFormVideo: weightedShortForm * typeCosts.shortFormVideo,
      },
      perTypeCosts: typeCosts,
    }
  }, [
    creditUsage,
    blogCount,
    emailCount,
    isOnPioneer,
    isTrialing,
    hasAnimatedImage,
    hasStylizedImage,
    workspace?.auto_reload_limit,
    totalStillImages,
    totalCarousels,
    totalVideos,
    weightedShortForm,
    storyCount,
    typeCosts,
  ])

  return {
    needsCredits,
    creditsNeeded,
    creditsRemaining,
    autopilotContentCreditsPrice,
    isProcessing,
    creditsNeededPerContentType,
    perTypeCosts,
  }
}

export const useNeedsCreditsToGenerateContent = (
  socialCount?: number,
  blogCount?: number,
  emailCount?: number,
  shortFormVideoCount?: number,
) => {
  const {
    socialCount: scheduleSocialCount,
    blogCount: scheduleBlogCount,
    emailCount: scheduleEmailCount,
    shortFormVideoCount: scheduleShortFormVideoCount,
  } = useCurrentScheduleCounts()
  return useCreditsToGenerateContent(
    socialCount ?? scheduleSocialCount,
    blogCount ?? scheduleBlogCount,
    emailCount ?? scheduleEmailCount,
    shortFormVideoCount ?? scheduleShortFormVideoCount,
  )
}
